<template>
  <div>
    <Head title="Verifikasi Laporan Penggunaan Anggaran Beasiswa" />
    <h1 class="mb-8 text-2xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/verify-accountability-reports">
        Verifikasi Laporan Penggunaan Anggaran Beasiswa {{ jenisBeasiswa }}
      </Link>
      <span class="font-medium text-indigo-400">/</span>
      {{ application.nama }}
    </h1>
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="flex items-center justify-between w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">Informasi Laporan Penggunaan Anggaran Beasiswa</h2>
        <a :href="`/verified-final-reports/${application.id}/download-files`" class="btn-indigo">
          Download Semua File
        </a>
      </div>
      <div class="flex w-full">
        <dl class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-3/12">NIM Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nim }}</dd>
          <dt class="w-full lg:w-3/12">Nama Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama }}</dd>
          <dt class="w-full lg:w-3/12">Nama Universitas</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_universitas }}</dd>
          <dt class="w-full lg:w-3/12">Program Studi</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_prodi }}</dd>
          <dt class="w-full lg:w-3/12">Jenis Beasiswa</dt>
          <dd class="w-full lg:w-9/12">: {{ jenisBeasiswa }} {{ application.jenis_pt }} {{ application.jenis }}</dd>
        </dl>
        <div class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-4/12">Semester</dt>
          <dd class="w-full lg:w-8/12">: {{ application.semester }}</dd>
          <dt class="w-full lg:w-4/12">Nilai IPK</dt>
          <dd class="w-full lg:w-8/12">: {{ application.ipk }}</dd>
          <dt class="w-full lg:w-4/12">No Rekening</dt>
          <dd class="w-full lg:w-8/12">: {{ application.account_number }}</dd>
          <dt class="w-full lg:w-4/12">Nominal UKT</dt>
          <dd class="w-full lg:w-8/12">:  {{ formatCurrency(application.nilai_ukt) }}</dd>
          <dt class="w-full lg:w-4/12">Nominal Beasiswa</dt>
          <dd class="w-full lg:w-8/12">:  {{ formatCurrency(nominalBeasiswa) }}</dd>
          <dt class="w-full lg:w-4/12">Total Anggaran</dt>
          <dd class="w-full lg:w-8/12">: {{ formatCurrency(totalAnggaran) }}</dd>
        </div>
      </div>
    </div>
    <!-- Informasi Perubahan UKT -->
    <div v-if="application.ukt_nominal" class="w-full my-8 bg-white rounded-md shadow">
      <div class="flex justify-between w-full px-8 py-6 bg-yellow-100">
        <h2 class="text-xl font-semibold text-yellow-800">Informasi Perubahan UKT</h2>
      </div>
      <div class="flex flex-wrap w-full p-6 leading-10">
        <dt class="w-full font-medium lg:w-3/12">Nominal UKT Baru</dt>
        <dd class="w-full lg:w-9/12">: {{ formatCurrency(application.ukt_nominal) }}</dd>
        <dt class="w-full font-medium lg:w-3/12">Catatan</dt>
        <dd class="w-full lg:w-9/12">: {{ application.ukt_catatan ?? '-' }}</dd>
        <dt class="w-full font-medium lg:w-3/12">Bukti Perubahan UKT</dt>
        <dd class="w-full lg:w-9/12">
          <span>: </span>
          <Button
            v-if="application.ukt_bukti_path"
            type="button"
            class="btn-green-sm"
            @click.prevent="viewImage(application.ukt_bukti_path)"
          >
            <font-awesome-icon icon="fas fa-magnifying-glass" class="mr-1" /> Lihat Bukti
          </Button>
          <span v-else>-</span>
        </dd>
      </div>
    </div>

    <div class="w-full my-8 bg-white rounded-md shadow">
      <div class="flex justify-between w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">Rincian Laporan Penggunaan Anggaran</h2>
      </div>

      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">Jenis </th>
            <th class="px-6 pt-6 pb-4">Uraian</th>
            <th class="px-6 pt-6 pb-4 text-right">Nominal RAB</th>
            <th class="px-6 pt-6 pb-4 text-right">Nominal PJ</th>
            <th class="px-6 pt-6 pb-4 text-right">Nominal Verifikasi</th>
            <th class="px-6 pt-6 pb-4 text-right">Status</th>
            <th v-if="canWrite('verify permohonan beasiswa')" class="px-6 pt-6 pb-4">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="detail in application.details" :key="detail.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="p-2 pl-6 border-t">
              <p class="font-semibold">{{ detail.category }}</p>
            </td>
            <td class="p-2 border-t">
              {{ detail.description }}
            </td>
            <td class="p-2 text-right border-t">
              {{ formatCurrency(detail.nominal_rab) }}
            </td>
            <td class="p-2 text-right border-t">
              {{ formatCurrency(detail.nominal_spj) }}
            </td>
            <td class="p-2 text-right border-t">
              {{ formatCurrency(detail.nominal_verifikasi) }}
            </td>
            <td class="p-4 text-center border-t">
              <div>
                <span v-if="detail.status == 5" class="px-4 py-2 text-sm bg-yellow-500 rounded-lg">Belum Verifikasi</span>
                <span v-else-if="detail.status == 6" class="px-4 py-2 text-sm bg-red-500 rounded-lg">Tidak Memenuhi</span>
                <span v-else class="px-4 py-2 text-sm bg-green-500 rounded-lg">Disetujui</span>
              </div>
              <p class="mt-6 leading-4">{{ detail.catatan_verifikasi }}</p>
            </td>
            <td v-if="canWrite('verify permohonan beasiswa')" class="w-px p-4 border-t ">
              <Button
                type="button" title="Lihat File" :class="{ 'rounded-l': detail.status !== 1 }"
                class="rounded-r btn-green-sm" @click.prevent="viewImage(detail.file_path)"
              >
                <font-awesome-icon icon="fas fa-magnifying-glass" />
              </Button>
            </td>
          </tr>
          <tr v-if="application.details.length === 0">
            <td class="px-6 py-4 border-t" :colspan="can('write permohonan beasiswa')? 5 : 4">Tidak ada data penggunaan anggaran yang diupload.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="w-full bg-white rounded-md shadow">
      <div class="flex justify-between w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">File Persyaratan</h2>
      </div>
      <table v-if="!statusInput" class="w-full whitespace-nowrap">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">Jenis File</th>
            <th class="px-6 pt-6 pb-4 text-center">Status Verifikasi</th>
            <th v-if="canWrite('verify permohonan beasiswa')" class="px-6 pt-6 pb-4 border-b">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in application.files" :key="file.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="p-4 border-t">
              {{ file.persyaratan }}
            </td>
            <td class="p-4 text-center border-t">
              <div>
                <span v-if="file.status == 5" class="px-4 py-2 text-sm bg-yellow-500 rounded-lg">Belum Verifikasi</span>
                <span v-else-if="file.status == 6" class="px-4 py-2 text-sm bg-red-500 rounded-lg">Tidak Memenuhi</span>
                <span v-else class="px-4 py-2 text-sm bg-green-500 rounded-lg">Disetujui</span>
              </div>
              <p class="mt-6 leading-4">{{ file.catatan_verifikasi }}</p>
            </td>
            <td v-if="canWrite('verify permohonan beasiswa')" class="w-px p-4 border-t">
              <Button
                type="button" title="Lihat File" :class="{ 'rounded-l': file.status !== 1 }"
                class="rounded-r btn-green-sm" @click.prevent="viewImage(file.file_path)"
              >
                <font-awesome-icon icon="fas fa-magnifying-glass" />
              </Button>
            </td>
          </tr>
          <tr v-if="application.files.length === 0">
            <td class="px-6 py-4 border-t" colspan="3">Tidak ada data file persayaratan yang diupload.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="w-full mt-8 bg-white rounded-md shadow">
      <div class="w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">History Permohonan Beasiswa</h2>
      </div>
      <table class="w-full">
        <thead>
          <tr class="font-bold text-left">
            <th class="w-3/12 px-6 pt-6 pb-4">Waktu</th>
            <th class="w-3/12 px-6 pt-6 pb-4 text-center">Jenis Aksi</th>
            <th class="w-6/12 px-6 pt-6 pb-4 border-b">Catatan</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="history in application.histories" :key="history.id"
            class="hover:bg-gray-100 focus-within:bg-gray-100"
          >
            <td class="p-4 border-t">
              {{ history.waktu }}
            </td>
            <td class="p-4 text-center border-t">
              {{ history.action }}
            </td>
            <td class="w-px p-4 border-t">
              {{ history.catatan }}
            </td>
          </tr>
          <tr v-if="application.histories.length === 0">
            <td class="px-6 py-4 border-t" colspan="3">Tidak ada data history.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <image-viewer :src="imageSource" :show-modal="showImage" @close="showImage = false" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import ImageViewer from '../../Shared/ImageViewer.vue'

export default {
  components: {
    Head,
    Link,
    ImageViewer,
  },
  layout: Layout,
  props: {
    application: Object,
    jenisBeasiswa: String,
    nominalBeasiswa: Number,
  },
  remember: 'form',
  data() {
    return {
      imageData: '',
      imageSource: '',
      showImage: false,
    }
  },
  computed: {
    totalAnggaran() {
      return this.application.details.reduce((sum, detail) => sum + (Number(detail.nominal_rab) || 0), 0)
    },
  },

  methods: {
    viewImage(imgSource) {
      this.imageSource = imgSource
      this.showImage = true
    },
    formatCurrency(value) {
      if (value === null || value === undefined) {
        return 'Rp 0'
      }
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },
  },
}
</script>
